import * as React from "react";
import { observer } from "mobx-react";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import {
  Box,
  Typography,
  MenuItem,
  ListItemIcon,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Theme, alpha, useTheme } from "@mui/material/styles";
import {
  DeleteIcon,
  RestoreFromTrashIcon as RestoreIcon,
} from "../../../icons";
import {
  CrudRow,
  RowCrudStatus,
  originalDeletedStatusList,
  actualStatusList,
  isOriginalCrudStatus,
  GridColumn,
  GridChangeSet,
} from "../types";
import { IMenuAction } from "../../../common/controls";
import { LoadFileButton } from "../../../common/controls/buttons/LoadFileButton";
import { useRootStore } from "../../../store/contexts";
import { FileIcon } from "../../../common/components/FileIcon";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

type LoadFilesCRUDCompactProps = {
  label: string;
  rows: CrudRow[];
  columns: GridColumn[];
  onCommitChanges: (changes: GridChangeSet) => void;
  onRestore: (id: string) => void;
  onLoadFile: (file: any) => void;
  menuActions?: IMenuAction[];
  showLabel?: boolean;
  showAddCommand?: boolean;
  showDeleteCommand?: boolean;
  maxRows?: number;
  dummyCRUDRestoredObservable: number;
  disabled?: boolean;
  hidden?: boolean;
  acceptFileTypes?: string;
  strictAccept?: boolean;
  description?: string;
  error?: string;
};

const getRowBgColor = (status: RowCrudStatus, theme: Theme): string => {
  if (status === "added") return alpha(theme.palette.success.main, 0.22);
  if (
    ["original-deleted", "original-updated-deleted", "added-deleted"].includes(
      status
    )
  )
    return alpha(theme.palette.error.main, 0.22);
  if (["original-updated", "added-updated"].includes(status))
    return alpha(theme.palette.warning.main, 0.22);
  return "";
};

export const LoadFilesCRUDCompact: React.FC<LoadFilesCRUDCompactProps> =
  observer(
    ({
      label,
      rows,
      columns,
      onCommitChanges,
      onRestore,
      onLoadFile,
      menuActions = [],
      showLabel = true,
      showAddCommand = true,
      showDeleteCommand = true,
      maxRows,
      dummyCRUDRestoredObservable,
      disabled = false,
      hidden = false,
      acceptFileTypes,
      strictAccept,
      description,
      error,
    }) => {
      const theme = useTheme();
      if (hidden) return null;
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const gridRows = React.useMemo(() => {
        return (
          Array.isArray(rows) ? rows.slice() : Object.values(rows)
        ) as CrudRow[];
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sentinel observable forces re-render on CRUD ops
      }, [rows, dummyCRUDRestoredObservable]);

      const mrtColumns = React.useMemo(() => {
        const dataColumns = columns.filter(
          (col) => !col.name.startsWith("__action")
        );
        return dataColumns.map((col) => {
          if (col.name === "mimetype") {
            return {
              accessorKey: col.name,
              header: col.title || col.name,
              size: 60,
              Cell: ({ row }: any) => (
                <FileIcon
                  filename={row.original.name}
                  mimetype={row.original.mimetype}
                  size={32}
                />
              ),
            };
          }
          if (col.name === "name") {
            // Nombre + afordancias INLINE (mismas acciones del menú: Ver para
            // PDFs, Descargar) — como en las grillas de documentos. Solo filas
            // ya guardadas (original).
            return {
              accessorKey: col.name,
              header: col.title || col.name,
              size: 300,
              grow: true,
              Cell: ({ row }: any) => {
                const rowData = row.original;
                const showInline = isOriginalCrudStatus(rowData.__status__);
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={rowData.name}
                    >
                      {rowData.name}
                    </Box>
                    {showInline &&
                      menuActions.map((action, index) => {
                        const enabled = action.isActionEnabled
                          ? action.isActionEnabled(rowData)
                          : true;
                        if (!enabled) return null;
                        return (
                          <Tooltip
                            key={`inline-${index}`}
                            title={action.label}
                            placement="top"
                            arrow
                          >
                            <IconButton
                              size="small"
                              sx={{ flexShrink: 0 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                action.onClick(rowData, e);
                              }}
                            >
                              {action.icon}
                            </IconButton>
                          </Tooltip>
                        );
                      })}
                  </Box>
                );
              },
            };
          }
          return {
            accessorKey: col.name,
            header: col.title || col.name,
            size: col.name === "formattedSize" ? 80 : 300,
            grow: col.name === "name",
          };
        }) as MRT_ColumnDef<any>[];
      }, [columns, menuActions]);

      const handleDeleteRow = (rowId: string) => {
        const changes: GridChangeSet = {
          deleted: [rowId],
        };
        onCommitChanges(changes);
      };

      const canAddRow = React.useMemo(() => {
        if (maxRows && showAddCommand) {
          const actualRows = gridRows.filter((x: CrudRow) =>
            actualStatusList.includes(x.__status__)
          );
          return actualRows.length < maxRows;
        }
        return showAddCommand;
      }, [gridRows, maxRows, showAddCommand]);

      return (
        <Box>
          <MaterialReactTable
            layoutMode="grid"
            columns={mrtColumns}
            data={gridRows}
            enableEditing={false}
            enableRowActions
            positionActionsColumn="first"
            enableColumnActions={false}
            enableColumnFilters={false}
            enablePagination={false}
            enableSorting={false}
            enableBottomToolbar={false}
            enableTopToolbar={true}
            enableGlobalFilter={false}
            enableDensityToggle={false}
            initialState={{
              density: "compact",
            }}
            displayColumnDefOptions={{
              "mrt-row-actions": {
                header: langStore.actionsLabel,
                size: 80,
                minSize: 80,
                maxSize: 80,
                grow: false,
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                verticalAlign: "middle",
              },
            }}
            renderTopToolbarCustomActions={() => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}>
                {canAddRow ? (
                  <LoadFileButton
                    label={`${langStore.addCommand} ${label}`}
                    onChange={(file: File) => onLoadFile(file)}
                    disabled={disabled}
                    acceptFileTypes={acceptFileTypes}
                    strictAccept={strictAccept}
                  />
                ) : (
                  showLabel && (
                    <Typography
                      sx={{ fontSize: 15, fontWeight: 500 }}
                      component="div"
                    >
                      <FieldLabelWithHelp
                        label={`${label}:`}
                        description={description}
                      />
                    </Typography>
                  )
                )}
              </Box>
            )}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                backgroundColor: getRowBgColor(row.original.__status__, theme),
              },
            })}
            renderRowActionMenuItems={({ row, closeMenu }) => {
              const rowData = row.original;
              const isDeleted = originalDeletedStatusList.includes(
                rowData.__status__
              );

              return [
                ...(isDeleted
                  ? []
                  : [
                      ...(showDeleteCommand
                        ? [
                            <MenuItem
                              key="delete"
                              onClick={() => {
                                handleDeleteRow(rowData.id);
                                closeMenu();
                              }}
                            >
                              <ListItemIcon>
                                <DeleteIcon />
                              </ListItemIcon>
                              {langStore.deleteCommand}
                            </MenuItem>,
                          ]
                        : []),
                    ]),

                ...menuActions.map((action, index) => {
                  const isEnabled = action.isActionEnabled
                    ? action.isActionEnabled(rowData)
                    : true;

                  return (
                    <MenuItem
                      key={`action-${index}`}
                      disabled={!isEnabled}
                      onClick={(e) => {
                        action.onClick(rowData, e);
                        closeMenu();
                      }}
                    >
                      <ListItemIcon>{action.icon}</ListItemIcon>
                      {action.label}
                    </MenuItem>
                  );
                }),

                ...(isDeleted
                  ? [
                      <MenuItem
                        key="restore"
                        onClick={() => {
                          onRestore(rowData.id);
                          closeMenu();
                        }}
                      >
                        <ListItemIcon>
                          <RestoreIcon />
                        </ListItemIcon>
                        {langStore.restoreCommand}
                      </MenuItem>,
                    ]
                  : []),
              ];
            }}
          />
          {error && (
            <Typography
              color="error"
              variant="caption"
              sx={{ marginLeft: "14px", marginTop: "3px", display: "block" }}
            >
              {error}
            </Typography>
          )}
        </Box>
      );
    }
  );
